import { useEffect } from 'react';

/**
 * Prerendered pages hydrate in steps (each Suspense boundary on its own), so "hydration is done"
 * is only known inside the page itself. Public layouts call useHydrated(); main.tsx registers what
 * should happen then (handing over text typed early, re-enabling form submits).
 */
let pending: (() => void) | null = null;

export function whenHydrated(callback: () => void): void {
  pending = callback;
}

export function useHydrated(): void {
  useEffect(() => {
    const callback = pending;
    pending = null;
    callback?.();
  }, []);
}

let resolveReady: () => void = () => undefined;
/** Resolves once the first screen is complete: its route rendered with its data (main.tsx). */
export const appReady = new Promise<void>((resolve) => {
  resolveReady = resolve;
});

export function markAppReady(): void {
  // tests/e2e/perf.spec.ts counts the scripts the first screen needed: those requested before this.
  if (performance.getEntriesByName('kd-app-ready').length === 0) performance.mark('kd-app-ready');
  // Answers boot.js fetched but the first screen did not need (its data came from the tab's
  // cache) are from page load; later requests must reach the server, not reuse them.
  window.__kdPrefetch = undefined;
  resolveReady();
}

/** Work that must not compete with the first screen: after it is ready, when the browser is idle. */
export function afterAppReady(work: () => void): void {
  void appReady.then(() => {
    // Safari has no requestIdleCallback; a timeout still lands after the current frame.
    if ('requestIdleCallback' in window) window.requestIdleCallback(work);
    else setTimeout(work, 1);
  });
}
